import React, { useState, useEffect } from 'react'
import * as api from '../../../lib/api'
import { useToast } from '../../../contexts/ToastContext'
import { 
  registerServiceWorker, 
  requestNotificationPermission, 
  getPermissionStatus, 
  subscribeToPushNotifications,
  fetchVapidPublicKey
} from '../../../lib/notifications'

export default function NotificationsSettings({ profile, setProfile }) {
  const toast = useToast()
  const [permission, setPermission] = useState(getPermissionStatus())
  const [saving, setSaving] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)

  // Дефолтные настройки, если в профиле ещё нет поля notification_settings
  const defaultSettings = {
    payments: true,
    chats: true,
    registrations: true,
    schedule: true,
    shifts: true,
    tasks: true,
    disputes: true,
    supply_requests: true,
    // Гранулярные настройки контроля смен (Push V1D-A & V1D-C)
    shift_started: true,
    shift_ended: true,
    shift_break_started: true,
    shift_break_ended: true,
    shift_break_reminder: true,
    daily_summary: true
  }

  const settings = {
    ...defaultSettings,
    ...(profile?.notification_settings || {})
  }

  useEffect(() => {
    // Проверка устройства iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
    setIsIOS(ios)
    
    // Проверка режима standalone (установлено ли PWA)
    const standalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches
    setIsStandalone(standalone)
  }, [])

  const updatePermissionStatus = () => {
    setPermission(getPermissionStatus())
  }

  const handleEnableNotifications = async () => {
    if (!('Notification' in window)) {
      toast.error('Уведомления не поддерживаются вашим браузером')
      return
    }

    try {
      const reg = await registerServiceWorker()
      if (!reg) {
        toast.error('Не удалось зарегистрировать Service Worker')
        return
      }

      const status = await requestNotificationPermission()
      updatePermissionStatus()

      if (status === 'granted') {
        const sub = await subscribeToPushNotifications(reg)
        if (sub) {
          toast.success('Уведомления успешно включены на этом устройстве!')
        } else {
          const config = await fetchVapidPublicKey()
          if (!config?.enabled) {
            toast.info('Разрешение получено, но аккаунт пока не включён в пилотную группу пуш-уведомлений.')
          } else {
            toast.warning('Права получены, но не удалось зарегистрировать подписку на сервере.')
          }
        }
      } else if (status === 'denied') {
        toast.error('Вы заблокировали уведомления в настройках браузера. Разрешите их вручную.')
      }
    } catch (err) {
      console.error('[NotificationsSettings] Enable error:', err)
      toast.error('Произошла ошибка при включении уведомлений')
    }
  }

  const handleToggle = async (key) => {
    if (saving) return

    const currentVal = settings[key] !== false
    const newSettings = {
      ...defaultSettings,
      ...(profile?.notification_settings || {}),
      [key]: !currentVal
    }

    setSaving(true)
    try {
      await api.updateProfile(profile.id, {
        notification_settings: newSettings
      })
      
      // Обновляем состояние в глобальном контексте AuthContext
      setProfile({
        ...profile,
        notification_settings: newSettings
      })
      
      toast.success('Настройки уведомлений обновлены')
    } catch (err) {
      console.error('[NotificationsSettings] Update error:', err)
      toast.error('Не удалось сохранить изменения')
    } finally {
      setSaving(false)
    }
  }

  const getPermissionLabel = () => {
    switch (permission) {
      case 'granted':
        return <span className="notification-status-indicator status-granted"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:'4px',verticalAlign:'middle'}}><polyline points="20 6 9 17 4 12"/></svg> Разрешено</span>
      case 'denied':
        return <span className="notification-status-indicator status-denied"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:'4px',verticalAlign:'middle'}}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Заблокировано</span>
      case 'unsupported':
        return <span className="notification-status-indicator status-denied"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:'4px',verticalAlign:'middle'}}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> Не поддерживается</span>
      default:
        return <span className="notification-status-indicator status-default"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:'4px',verticalAlign:'middle'}}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> Не настроено</span>
    }
  }

  // Фильтруем список настроек в зависимости от роли пользователя
  const userRole = (profile?.role || '').toLowerCase().trim()
  const isOwner = userRole === 'owner'
  const canReceiveManagement = ['owner', 'admin', 'admin_employee'].includes(userRole)
  const canWorkShift = ['employee', 'helper', 'admin', 'admin_employee', 'owner'].includes(userRole)
  const showShiftsSection = canReceiveManagement || canWorkShift

  const managementSubItems = [
    {
      key: 'shift_started',
      title: 'Начало смены',
      desc: 'Сотрудник открыл смену на ПВЗ'
    },
    {
      key: 'shift_ended',
      title: 'Закрытие смены',
      desc: 'Сотрудник закрыл смену и завершил работу'
    },
    {
      key: 'shift_break_started',
      title: 'Уход на перерыв',
      desc: 'Сотрудник ушел на перерыв в течение смены'
    },
    {
      key: 'shift_break_ended',
      title: 'Возврат с перерыва',
      desc: 'Сотрудник вернулся с перерыва к работе'
    },
    {
      key: 'daily_summary',
      title: 'Итоги дня',
      desc: 'Вечерняя сводка по рабочим сменам ПВЗ'
    }
  ]

  const personalSubItems = [
    {
      key: 'shift_break_reminder',
      title: 'Напоминание о перерыве',
      desc: 'Личное напоминание, если перерыв длится более 5 минут'
    }
  ]

  const items = [
    {
      key: 'chats',
      title: 'Сообщения в чатах',
      desc: 'Личные сообщения и новые публикации в каналах',
      show: true
    },
    {
      key: 'schedule',
      title: 'Рабочий график',
      desc: 'Создание, изменение или удаление смен на календаре',
      show: true
    },
    {
      key: 'tasks',
      title: 'Задачи и поручения',
      desc: 'Назначение задач, изменения статусов, новые комментарии',
      show: true
    },
    {
      key: 'payments',
      title: 'Выплаты и начисления',
      desc: 'Выплата зарплаты, премии, штрафы и удержания',
      show: true
    },
    {
      key: 'disputes',
      title: 'Споры по начислениям',
      desc: 'Оспаривание штрафов, удержаний и решения по спорам',
      show: true
    },
    {
      key: 'supply_requests',
      title: 'Заявки на снабжение',
      desc: 'Создание заявок и изменение их статусов (одобрено/оплачено)',
      show: true
    },
    {
      key: 'registrations',
      title: 'Заявки на регистрацию',
      desc: 'Регистрация новых сотрудников в вашей организации',
      show: isOwner
    },
    {
      key: 'shifts',
      title: canReceiveManagement ? 'Контроль рабочих смен' : 'Рабочие смены и перерывы',
      desc: canReceiveManagement ? 'Открытие/закрытие смен сотрудниками, уходы на перерывы' : 'Напоминания о перерывах и статус смены',
      show: showShiftsSection
    }
  ]

  return (
    <div className="settings-section animate-fade-in">
      <h3 className="settings-section-title">Системные Push-уведомления</h3>
      
      {/* Карточка статуса разрешений в браузере */}
      <div className="notification-status-box">
        <div className="notification-status-header">
          <div>
            <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)' }}>
              Статус на этом устройстве
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
              Для работы системных пушей необходимо предоставить права браузеру
            </div>
          </div>
          {getPermissionLabel()}
        </div>

        {permission !== 'granted' && (
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={handleEnableNotifications}
            style={{ width: '100%' }}
          >
            Включить уведомления на этом устройстве
          </button>
        )}

        {/* Подсказка для iOS Safari */}
        {isIOS && !isStandalone && (
          <div className="notification-ios-hint">
            <strong>Важно для пользователей iOS:</strong><br />
            На iPhone уведомления работают только при установке приложения на экран «Домой».<br />
            1. Нажмите на иконку <strong>«Поделиться»</strong> (квадрат со стрелкой внизу браузера Safari).<br />
            2. Выберите пункт <strong>«На экран "Домой"»</strong>.<br />
            3. Запустите приложение с рабочего стола и включите уведомления здесь заново.
          </div>
        )}
      </div>

      <h3 className="settings-section-title" style={{ marginTop: '12px' }}>Настройки подписок</h3>
      <p className="settings-subtitle" style={{ marginTop: '-8px' }}>
        Выберите, о каких событиях вы хотите получать уведомления на свои устройства
      </p>

      {/* Список тумблеров */}
      <div className="notifications-list">
        {items.filter(item => item.show).map(item => (
          <React.Fragment key={item.key}>
            <div className="notification-item-card">
              <div className="notification-item-info">
                <div className="notification-item-title">{item.title}</div>
                <div className="notification-item-desc">{item.desc}</div>
              </div>
              
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={settings[item.key] !== false} 
                  disabled={saving}
                  onChange={() => handleToggle(item.key)} 
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {/* Вложенная группа гранулярных настроек смен (Push V1D-A) */}
            {item.key === 'shifts' && (canReceiveManagement || canWorkShift) && (
              <div className={`notification-sub-group ${!settings.shifts ? 'is-disabled' : ''}`}>
                {!settings.shifts && (
                  <div className="notification-sub-group-hint">
                    Включите «Контроль рабочих смен», чтобы настроить отдельные события
                  </div>
                )}

                {/* Управленческие события (для руководства) */}
                {canReceiveManagement && (
                  <>
                    <div className="notification-sub-group-section-title">
                      События сотрудников
                    </div>
                    {managementSubItems.map(sub => (
                      <div key={sub.key} className="notification-sub-item">
                        <div className="notification-sub-info">
                          <div className="notification-sub-title">{sub.title}</div>
                          <div className="notification-sub-desc">{sub.desc}</div>
                        </div>
                        <label className="toggle-switch" style={{ margin: 0, flexShrink: 0 }}>
                          <input 
                            type="checkbox" 
                            checked={settings[sub.key] !== false} 
                            disabled={saving || !settings.shifts}
                            onChange={() => handleToggle(sub.key)} 
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                    ))}
                  </>
                )}

                {/* Личные уведомления (для ролей, выходящих на смену) */}
                {canWorkShift && (
                  <>
                    {canReceiveManagement && (
                      <div className="notification-sub-group-section-title">
                        Мои уведомления
                      </div>
                    )}
                    {personalSubItems.map((sub) => (
                      <div key={sub.key} className="notification-sub-item">
                        <div className="notification-sub-info">
                          <div className="notification-sub-title">{sub.title}</div>
                          <div className="notification-sub-desc">{sub.desc}</div>
                        </div>
                        <label className="toggle-switch" style={{ margin: 0, flexShrink: 0 }}>
                          <input 
                            type="checkbox" 
                            checked={settings[sub.key] !== false} 
                            disabled={saving || !settings.shifts}
                            onChange={() => handleToggle(sub.key)} 
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

          </React.Fragment>
        ))}
      </div>
    </div>
  )
}
